import { useEffect, useMemo } from 'react';
import { Tree } from 'react-arborist';
import type { VoidFunctionComponent } from 'react';
import type { NodeApi } from 'react-arborist';

import SnippetsTreeViewNode from '../../TreeView/Node';
import type { SnippetMatch } from '../../../../../../shared/snippets';
import type { SnippetTreeNodeData } from '../../types';

import buildTreeFromMatches, { findFirstFilePath } from './buildTreeFromMatches';

const TREE_HEIGHT = 800;

type Props = {
  /**
   * A list of matches for a given snippet
   */
  matches: SnippetMatch[];
  /**
   * The selected node in the tree
   */
  selectedPath: string | null;
  /**
   * Set the selectedPath when a node is clicked on.
   * @param path The path which is selected
   */
  setSelectedPath: (path: string) => void;
  /**
   * The ID of the current snippet (used to reset selection when snippet changes)
   */
  selectedSnippetId: string;
};

/**
 * Tree view for the snippets drawer code view.
 */
const SnippetsDrawerCodeFileTree: VoidFunctionComponent<Props> = ({
  matches,
  selectedPath,
  setSelectedPath,
  selectedSnippetId,
}) => {
  //  Memoized since this is a somewhat expensive task (with larger trees)
  const data = useMemo(() => buildTreeFromMatches(matches), [matches]);

  //  Auto-select the first file path when the tree is initially rendered or when snippet changes
  useEffect(() => {
    if (data.length > 0) {
      const firstFilePath = findFirstFilePath(data);
      if (firstFilePath) {
        setSelectedPath(firstFilePath);
      }
    }
  }, [data, setSelectedPath, selectedSnippetId]);

  return (
    <Tree<SnippetTreeNodeData>
      // ref={treeRef}
      data={data}
      openByDefault
      idAccessor="path" // Tells it we want to use the "path" as the ID
      childrenAccessor="children" // Tells it we want to use the "children" as the children
      //  Layout
      rowHeight={32}
      indent={8}
      height={TREE_HEIGHT}
      //  Selection logic -- This will be added as part of the next ticket (CORE-5376)
      selection={selectedPath || undefined}
      onSelect={(nodes: Array<NodeApi<SnippetTreeNodeData>>) => {
        //  react-arborist allows mutli-select, which is why it passes us an array of nodes.
        //  We only want to select the first node, so we take the first one.
        const node = nodes[0];
        //  Only allow files to be selected.
        if (node && node.data.type === 'file') {
          setSelectedPath(node.data.path);
        }
      }}
      disableMultiSelection
      disableDrag
    >
      {SnippetsTreeViewNode}
    </Tree>
  );
};

export default SnippetsDrawerCodeFileTree;
